/*
 * Copyright (c) 2022, Salesforce, Inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React, {useMemo, useRef, useState} from 'react'
import {FormattedMessage} from 'react-intl'
import PropTypes from 'prop-types'
import {useIntl} from 'react-intl'

import {
    AspectRatio,
    Box,
    Flex,
    Skeleton as ChakraSkeleton,
    Stack,
    IconButton,
    Button,
    Text,
    useMultiStyleConfig,
    useDisclosure,
    useBreakpointValue
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import Link from '@salesforce/retail-react-app/app/components/link'
import DynamicImage from '@salesforce/retail-react-app/app/components/dynamic-image'
import {getUpdateBundleChildArray} from '@salesforce/retail-react-app/app/utils/product-utils'
import {getAssetUrl} from '@salesforce/pwa-kit-react-sdk/ssr/universal/utils'
import {useToast} from '@salesforce/retail-react-app/app/hooks/use-toast'
import {useCurrency} from '@salesforce/retail-react-app/app/hooks'
import {useProduct, useShopperBasketsMutationHelper} from '@salesforce/commerce-sdk-react'
import {getPriceData} from '@salesforce/retail-react-app/app/utils/product-utils'
import {useProducts, useShopperBasketsMutation} from '@salesforce/commerce-sdk-react'
import {useSiteCustomPreferences} from '../../hooks/use-site-custom-preferences'
import {useTrackThinkAnalyticsEvents} from '../../hooks/use-track-thinkanalytics-events.js'
import {useCurrentCustomer} from '../../hooks/use-current-customer.js'
import {useCustomerGroups} from '../../hooks/use-customer-groups'
import useSegment from '../../integrations/segment/app/hooks/use-segment.js'
import {useGtm} from '../../integrations/gtm/app/hooks/use-gtm.js'
import {useMiniCartContext} from '../../hooks/use-mini-cart.js'
import {useCurrentBasket} from '../../hooks/use-current-basket'

import {productUrlBuilder} from '../../../app/utils/url.js'
import DisplayPrice from '../display-price'
import PromoCallout from './promo-callout'
import ProductSashes from './product-sashes'
import WishlistIcon from './wishListIcon.jsx'
import FilledWishlistIcon from './filledWishlistIcon.jsx'
import QuickViewModal from '../quick-view-modal'
import {formattedDate, getPageType, isCustomerInProductGroups} from '../../utils/custom-utils'
import {canAddToCart, getMaxOrderQuantity} from '../../utils/product-utils'
import {
    API_ERROR_MESSAGE,
    PRODUCT_TILE_IMAGE_VIEW_TYPE,
    UNCONFIRMED_DATE,
    TOAST_MESSAGE_ADDED_TO_CART
} from '../../constants'

// Product Tile Component Skeleton
export const Skeleton = () => {
    const styles = useMultiStyleConfig('ProductTile')
    return (
        <Box
            data-testid="sf-product-tile-skeleton"
            className="product-block"
            {...styles.tileStyles}
        >
            <Box className="product-tile" maxWidth="300px" margin="0 auto">
                <Stack spacing={2}>
                    <Box {...styles.imageWrapper}>
                        <AspectRatio ratio={1} {...styles.image}>
                            <ChakraSkeleton />
                        </AspectRatio>
                    </Box>
                    <ChakraSkeleton width="80%" height={{base: '30px', lg: '20px'}} />
                    <ChakraSkeleton width={'30%'} height="24px" />
                    <ChakraSkeleton width="100%" height={'10'} borderRadius={'8px'} mb={2} />
                    <ChakraSkeleton
                        width={{base: '120px', md: '220px'}}
                        height="12px"
                        mb={2}
                        mx={'auto'}
                    />
                </Stack>
            </Box>
        </Box>
    )
}

/**
 * The ProductTile is a simple visual representation of a
 * product object. It will show it's default image, name and price.
 * It also supports favourite products, controlled by a heart icon.
 */
const ProductTile = (props) => {
    const {getcustomPreferencevalue} = useSiteCustomPreferences()
    const {openMiniCartOnAddToCart} = useMiniCartContext()
    const {data: customer} = useCurrentCustomer()
    const {data: customerGroups} = useCustomerGroups(customer?.isGuest)
    const customerGroupList = customer?.c_customerGroups || customerGroups?.result
    const {currency} = useCurrency()
    const segment = useSegment()
    const gtm = useGtm()
    const configs = getConfig()
    const {formatMessage} = useIntl()

    const {addItemToNewOrExistingBasket} = useShopperBasketsMutationHelper()
    const updateItemsInBasketMutation = useShopperBasketsMutation('updateItemsInBasket')

    const {
        product,
        enableFavourite = false,
        showHorizontalTileInMobile = false,
        isFavourite,
        onFavouriteToggle,
        dynamicImageProps,
        source = 'CRSTORE',
        productPosition,
        ...rest
    } = props
    const {isOpen, onOpen, onClose} = useDisclosure()

    const isFavouriteLoading = useRef(false)
    const styles = useMultiStyleConfig('ProductTile')
    const powerReviewOverride = useMultiStyleConfig('PowerReviewOverride')
    const isTAEnabled = getcustomPreferencevalue('pwaThinkAnalyticsEnabled')
    const {mutate: trackTAEvent} = useTrackThinkAnalyticsEvents()

    const isMobile = useBreakpointValue(
        {
            base: true,
            sm: false
        },
        'sm' // 'sm' as the default/fallback value
    )

    const powerReviewStyles = {
        '.p-w-r .pr-star-v4-100-filled': {
            backgroundImage: `url(${getAssetUrl('static/img/rating-100.svg')})`
        }
    }

    const {image, productId} = product
    const bundleChildProductImages = product?.imageGroups?.find(
        (group) => group.viewType === PRODUCT_TILE_IMAGE_VIEW_TYPE && group.images?.length > 0
    )?.images?.[0]
    const {disWidth = 300} = dynamicImageProps || {}
    const baseUrl =
        image?.disBaseLink ||
        image?.link ||
        product?.image?.disBaseLink ||
        product?.image?.link ||
        bundleChildProductImages?.disBaseLink
    const paramSeparator = baseUrl?.includes('?') ? '&' : '?'
    const tileImageSource = `${baseUrl}${paramSeparator}sw=${disWidth}&q=60`

    const prodObj = {
        productName: product?.productName || product?.name,
        productId: product?.productId || product?.id
    }
    const productURL = productUrlBuilder(prodObj)
    let parameters = {
        id: product?.productId || product?.id,
        perPricebook: true,
        allImages: true
    }

    if (product?.type == 'bundle' || product?.productType == 'bundle') {
        parameters.expand = ['bundledProducts.product', 'variations']
    }

    const {data: productDetail} = useProduct(
        {
            parameters
        },
        {
            // When shoppers select a different variant (and the app fetches the new data),
            // the old data is still rendered (and not the skeletons).
            keepPreviousData: true
        }
    )

    const isMaster = !!(product?.productType?.master || product?.type?.master)
    const variationsIDS = isMaster
        ? product?.variants?.map((variant) => variant.productId).join(',') ?? ''
        : product?.productId
        ? product?.productId
        : product?.id
    var {data: fullProductDetails} = useProducts({
        parameters: {
            ids: variationsIDS,
            perPricebook: true,
            allImages: true
        }
    })
    const productCustomerGroups = productDetail?.c_customerGroups || []
    let isCustomerAllowed = isCustomerInProductGroups(productCustomerGroups, customerGroupList)

    // child IDs for bundle products
    const bundleChildVariantIds = Object.keys(productDetail?.bundledProducts || {})
        .map((key) => productDetail?.bundledProducts?.[key]?.product?.id)
        .filter(Boolean)
        .join(',')

    // Load child products
    const {data: bundleChildrenData} = useProducts(
        {
            parameters: {
                ids: bundleChildVariantIds,
                allImages: false,
                expand: ['variations']
            }
        },
        {
            enabled: !!bundleChildVariantIds && isCustomerAllowed
        }
    )

    if (isCustomerAllowed && bundleChildrenData?.data?.length > 0) {
        const restrictedChildFound = bundleChildrenData.data.some(
            (child) => !isCustomerInProductGroups(child?.c_customerGroups, customerGroupList)
        )

        if (restrictedChildFound) {
            isCustomerAllowed = false
        }
    }

    const isSoldOut =
        fullProductDetails?.data === undefined
            ? null
            : !fullProductDetails.data.some((fullProduct) => fullProduct?.inventory?.ats > 0)

    const priceData = useMemo(() => {
        return getPriceData(productDetail)
    }, [productDetail])

    // ProductTile is used by two components, RecommendedProducts and ProductList.
    // RecommendedProducts provides a localized product name as `name` and non-localized product
    // name as `productName`. ProductList provides a localized name as `productName` and does not
    // use the `name` property.
    const localizedProductName = product.name ?? product.productName

    /****************************** Basket *********************************/
    const {data: basket} = useCurrentBasket()
    const addItemToBasketMutation = useShopperBasketsMutation('addItemToBasket')

    const showToast = useToast()
    const showError = () => {
        showToast({
            title: formatMessage(API_ERROR_MESSAGE),
            status: 'error',
            isClosable: false
        })
    }

    /**************** Product ATC ****************/
    const maxOrderQuantity = getMaxOrderQuantity(productDetail, configs)
    const [isAddingToCart, setIsAddingToCart] = useState(false)
    const handleProductAddToCart = async (productDetail, event) => {
        if (isAddingToCart) return
        setIsAddingToCart(true)
        try {
            const quantity = productDetail?.minOrderQuantity || 1
            const isAddToCartAllowed = canAddToCart(
                productDetail,
                quantity,
                basket,
                maxOrderQuantity,
                showToast,
                formatMessage
            )
            if (!isAddToCartAllowed) {
                return null
            }
            const productSelectionValues = Object.values({productDetail})
            const productItems = productSelectionValues.map(() => ({
                productId: productSelectionValues[0]?.id,
                price: productSelectionValues[0]?.price,
                quantity: 1
            }))
            await addItemToBasketMutation.mutateAsync({
                parameters: {basketId: basket.basketId},
                body: productItems
            })
            showToast({
                title: formatMessage(TOAST_MESSAGE_ADDED_TO_CART),
                status: 'success',
                isClosable: false,
                duration: 3000
            })
            openMiniCartOnAddToCart() // Open MiniCart on ATC

            setTimeout(() => {
                // TA Track events
                trackTAEvent({
                    c_productId: productDetail?.id,
                    c_refUseCase: source,
                    c_useCase: 'Store%2FLearn%2FAddToCart'
                })

                // segment
                const currentElement = event?.currentTarget?.closest('.slick-slide')
                segment.sendAddToCart(productDetail, basket, event)
                if (currentElement || event?.target?.closest('.slick-slide')) {
                    segment.sendPanelSelectedEvent(event, productDetail, {})
                }
                // gtm
                gtm?.sendAddToCart('addToCart', productDetail)
            }, 0)

            return productSelectionValues
        } catch (error) {
            showError(error)
        } finally {
            setIsAddingToCart(false)
        }
    }
    /**************** End of Product ATC ****************/

    /**************** Quick View ATC ****************/
    const handleQuickViewAddToCart = async (productSelectionValues) => {
        const productItems = productSelectionValues.map(({variant, quantity}) => ({
            productId: variant.productId,
            price: variant.price,
            quantity
        }))

        await addItemToBasketMutation.mutateAsync({
            parameters: {basketId: basket.basketId},
            body: productItems
        })

        // gtm
        gtm.sendAddToCart('addToCart', productSelectionValues[0].product)
        // segment event
        segment.sendAddToCartFromQuickView(productSelectionValues[0].product, basket)

        // Open MiniCart on successful add to cart
        openMiniCartOnAddToCart()
        // TA event
        trackTAEvent({
            c_productId: productItems[0]?.productId,
            c_refUseCase: source,
            c_useCase: 'Store%2FLearn%2FAddToCart'
        })

        return productSelectionValues
    }
    /**************** END of Quick View ATC****************/

    /**************** Bundle Product ATC ****************/
    // Top level bundle does not have variants
    const handleProductBundleAddToCart = async (productDetail) => {
        if (isAddingToCart) return
        setIsAddingToCart(true)
        try {
            const quantity = productDetail?.minOrderQuantity || 1
            const isAddToCartAllowed = canAddToCart(
                productDetail,
                quantity,
                basket,
                maxOrderQuantity,
                showToast,
                formatMessage
            )
            if (!isAddToCartAllowed) {
                return null
            }
            const childProductSelections = Object.values({productDetail})
            const productItems = [
                {
                    productId: product?.productId,
                    price: product?.price,
                    quantity: 1,
                    // The add item endpoint in the shopper baskets API does not respect variant selections
                    // for bundle children, so we have to make a follow up call to update the basket
                    // with the chosen variant selections
                    bundledProductItems: childProductSelections[0]?.bundledProducts?.map(
                        (child) => {
                            return {
                                productId: child?.product?.id,
                                quantity: child?.quantity
                            }
                        }
                    )
                }
            ]
            const res = await addItemToNewOrExistingBasket(productItems)
            const bundleChildMasterIds = childProductSelections[0]?.bundledProducts?.map(
                (child) => {
                    return child?.product?.id
                }
            )
            // since the returned data includes all products in basket
            // here we compare list of productIds in bundleProductItems of each productItem to filter out the
            // current bundle that was last added into cart
            const currentBundle = res?.productItems?.find((productItem) => {
                if (!productItem?.bundledProductItems?.length) return
                const bundleChildIds = productItem?.bundledProductItems?.map((item) => {
                    // seek out the bundle child that still uses masterId as product id
                    return item?.productId
                })
                return bundleChildIds.every((id) => bundleChildMasterIds?.includes(id))
            })
            const itemsToBeUpdated = getUpdateBundleChildArray(
                currentBundle,
                childProductSelections
            )
            if (itemsToBeUpdated.length) {
                // make a follow up call to update child variant selection for product bundle
                // since add item endpoint doesn't currently consider product bundle child variants
                await updateItemsInBasketMutation.mutateAsync({
                    method: 'PATCH',
                    parameters: {
                        basketId: res?.basketId
                    },
                    body: itemsToBeUpdated
                })
            }
            // Show success toast
            showToast({
                title: formatMessage(TOAST_MESSAGE_ADDED_TO_CART), // You'll need to define this message
                status: 'success',
                isClosable: false,
                duration: 3000
            })
            // TA Track events
            trackTAEvent({
                c_productId: productDetail?.id || productDetail?.productId,
                c_refUseCase: source,
                c_useCase: 'Store%2FLearn%2FAddToCart'
            })

            segment.sendAddToCart(productDetail, basket, event)
            // Open MiniCart on successful add to cart
            openMiniCartOnAddToCart()
            return childProductSelections
        } catch (error) {
            showError(error)
        } finally {
            setIsAddingToCart(false)
        }
    }
    /**************** End of Bundle Product ATC ****************/

    /**************** Quick View ****************/
    const handleQuickViewButtonClick = (productDetail, event) => {
        onOpen()
        // segement event
        if (event?.target?.closest('.slick-slide')) {
            segment.sendPanelSelectedEvent(event, productDetail, {})
        }
    }
    /**************** End of Quick View ****************/

    /**************** End of Customer Restriction Message ****************/

    const isHorizontalTile = isMobile && showHorizontalTileInMobile
    return (
        <Box
            className="product-block"
            {...styles.tileStyles}
            position={isHorizontalTile ? {base: 'relative', sm: 'staic'} : undefined}
        >
            <Box
                className={`product-tile ${source}`}
                data-pid={productId ? productId : product?.id}
            >
                <Box display={isHorizontalTile ? {base: 'flex', sm: 'block'} : 'block'}>
                    <Box
                        {...{...styles.imageWrapper, className: 'product-tile-image-block'}}
                        position={isHorizontalTile ? undefined : 'relative'}
                        width={isHorizontalTile ? '160px' : undefined}
                        mb={isHorizontalTile ? '0' : '2'}
                    >
                        <Link
                            data-testid="product-tile"
                            to={productURL}
                            {...styles.link}
                            {...rest}
                            aria-label={`View image for ${localizedProductName}`}
                            display={'block !important'}
                            data-position={productPosition}
                        >
                            <AspectRatio
                                ratio={1}
                                display="flex"
                                alignItems="center"
                                className="image-container"
                                {...styles.image}
                            >
                                <DynamicImage
                                    data-testid="product-tile-image"
                                    borderRadius="8px"
                                    src={tileImageSource}
                                    imageProps={{
                                        // treat img as a decorative item, we don't need to pass `image.alt`
                                        // since it is the same as product name
                                        // which can cause confusion for inBoxiduals who uses screen readers
                                        alt: `${localizedProductName}`,
                                        loading: 'lazy',
                                        ...dynamicImageProps?.imageProps
                                    }}
                                    onClick={(event) => {
                                        // ThinkAnalytics
                                        const pageType = getPageType()
                                        if (pageType === 'Collections' && isTAEnabled) {
                                            trackTAEvent({
                                                c_productId: productId || product?.id,
                                                c_refUseCase: source,
                                                c_useCase: 'Store%2FLearn%2FClickedOnSearch',
                                                additionalData: {isSearch: true}
                                            })
                                        }
                                        // segement event
                                        segment.sendProductClick(
                                            productDetail,
                                            productPosition,
                                            event
                                        )
                                        segment.sendProductViewed(event, productDetail)
                                        segment.sendPanelSelectedEvent(event, productDetail, {})
                                    }}
                                />
                            </AspectRatio>
                        </Link>
                        {/* Wishlist */}
                        {enableFavourite && (
                            <Flex
                                onClick={(e) => {
                                    // stop click event from bubbling
                                    // to avoid user from clicking the underlying
                                    // product while the favourite icon is disabled
                                    e.preventDefault()
                                }}
                                className={'wishlistButton'}
                                justifyContent={'end'}
                                position="absolute"
                                right="8px"
                                top={isHorizontalTile ? 'auto' : '8px'}
                                bottom={isHorizontalTile ? '32px' : 'auto'}
                            >
                                <IconButton
                                    {...styles.favIcon}
                                    data-testid="wishlist-button"
                                    className="wishlist-icon"
                                    width={'44px'}
                                    height={'44px'}
                                    disabled={!basket?.basketId}
                                    aria-label={
                                        isFavourite
                                            ? formatMessage(
                                                  {
                                                      id: 'product_tile.assistive_msg.remove_from_wishlist',
                                                      defaultMessage:
                                                          'Remove {product} from wishlist'
                                                  },
                                                  {product: localizedProductName}
                                              )
                                            : formatMessage(
                                                  {
                                                      id: 'product_tile.assistive_msg.add_to_wishlist',
                                                      defaultMessage: 'Add {product} to wishlist'
                                                  },
                                                  {product: localizedProductName}
                                              )
                                    }
                                    icon={isFavourite ? <FilledWishlistIcon /> : <WishlistIcon />}
                                    onClick={async (event) => {
                                        if (!isFavouriteLoading.current) {
                                            isFavouriteLoading.current = true
                                            await onFavouriteToggle(
                                                !isFavourite,
                                                productDetail,
                                                event
                                            )
                                            isFavouriteLoading.current = false
                                        }
                                        // segement event
                                        if (event?.target?.closest('.slick-slide')) {
                                            segment.sendProductClick(
                                                productDetail,
                                                productPosition,
                                                event
                                            )
                                            segment.sendPanelSelectedEvent(event, productDetail, {})
                                        }
                                    }}
                                />
                            </Flex>
                        )}

                        {/*Desktop Product Sashes */}
                        {!isHorizontalTile && (
                            <Box
                                className="poduct-sashes"
                                position="absolute"
                                bottom="8px"
                                left="8px"
                            >
                                <ProductSashes product={product} />
                            </Box>
                        )}
                    </Box>

                    {/*Quick View Modal*/}
                    {isOpen && (
                        <QuickViewModal
                            isOpen={isOpen}
                            onOpen={onOpen}
                            onClose={onClose}
                            product={productDetail}
                            addToCart={(variant, quantity) =>
                                handleQuickViewAddToCart([
                                    {product: productDetail, variant, quantity}
                                ])
                            }
                        />
                    )}

                    <Box
                        className="product-tile-body"
                        width={isHorizontalTile ? 'calc(100% - 160px)' : ''}
                        pl={isHorizontalTile ? '2' : '0'}
                    >
                        {/* Title */}
                        <Link
                            data-testid="product-tile-name"
                            to={productURL}
                            {...rest}
                            aria-label={`View details for ${localizedProductName}`}
                            display="block"
                            onClick={(event) => {
                                // segement event
                                segment.sendProductClick(productDetail, productPosition, event)
                                segment.sendProductViewed(event, productDetail)
                            }}
                        >
                            <Text {...styles.title} noOfLines={2}>
                                {localizedProductName}
                            </Text>
                        </Link>

                        <Box mb={2}>
                            <Box
                                className="product-promotions"
                                overflow="hidden"
                                h={isHorizontalTile ? 'auto' : '16px'}
                                mb={isHorizontalTile ? 0 : 1}
                            >
                                <PromoCallout product={product} />
                            </Box>

                            <Box
                                display={isHorizontalTile ? 'flex' : 'flex'}
                                justifyContent={isHorizontalTile ? '' : 'space-between'}
                                flexDirection={isHorizontalTile ? 'column' : ''}
                            >
                                {/* Power Reviews */}
                                <Box
                                    className="pr-custom-ratings-plp"
                                    sx={{...powerReviewOverride, ...powerReviewStyles}}
                                    flex={isHorizontalTile ? '1' : ''}
                                    order={isHorizontalTile ? '0' : '2'}
                                >
                                    <Box
                                        id={
                                            'category-snippet-' +
                                            (product?.productId || product?.id || '')
                                        }
                                        data-product-id={product?.productId || product?.id || ''}
                                        className="category-snippet"
                                    />
                                </Box>

                                {/* Price */}
                                <Box
                                    className="product-prices"
                                    h="24px"
                                    overflow="hidden"
                                    flex={isHorizontalTile ? '1' : ''}
                                    order={1}
                                >
                                    <DisplayPrice
                                        priceData={priceData}
                                        currency={currency}
                                        isPLPPage={true}
                                    />
                                </Box>
                            </Box>
                        </Box>

                        {/* Customer Group Restriction Message */}
                        {/* {fullProductDetails?.data[0]?.c_customerGroups?.length > 0 && (
                        <Box
                            className="customerGroupRestrictionMessage"
                            style={{height: '25px', overflow: 'hidden', lineHeight: '25px'}}
                        >
                            <Text color="red">
                                <FormattedMessage
                                    defaultMessage="{message}"
                                    id="customer_group_restriction_message"
                                    values={{
                                        message: customerRestrictionMessage()
                                    }}
                                />
                            </Text>
                        </Box>
                    )} */}
                    </Box>
                </Box>
                {/* MObile Product Sashes */}
                {isHorizontalTile && (
                    <Box className="product-sashes" height={6} mt={'8px'} mb={'12px'}>
                        <ProductSashes product={product} />
                    </Box>
                )}

                {/* ATC & Select Size Button */}
                <Button
                    variant="secondaryFull"
                    onClick={(event) => {
                        if (
                            product?.productType?.master === true ||
                            product?.type?.master === true
                        ) {
                            handleQuickViewButtonClick(productDetail, event)
                        } else if (
                            product?.productType?.bundle === true ||
                            product?.type?.bundle === true
                        ) {
                            handleProductBundleAddToCart(productDetail)
                        } else {
                            handleProductAddToCart(productDetail, event)
                        }
                    }}
                    disabled={
                        isSoldOut !== false ||
                        isAddingToCart ||
                        !basket?.basketId ||
                        !isCustomerAllowed
                    }
                    isLoading={isAddingToCart}
                    className={'addtocartbutton non-pd-button'}
                    width={isHorizontalTile ? 'calc(100% - 56px)' : '100%'}
                >
                    <Box ml={1}>
                        {isMaster ? (
                            <FormattedMessage
                                defaultMessage="Select Size"
                                id="product_view.button.select_size"
                            />
                        ) : isSoldOut === null ? (
                            ''
                        ) : isSoldOut ? (
                            <FormattedMessage
                                defaultMessage="Out of stock"
                                id="use_product.message.out_of_stock"
                            />
                        ) : (
                            <FormattedMessage
                                defaultMessage="Add to Cart"
                                id="product_view.button.add_to_cart"
                            />
                        )}
                    </Box>
                </Button>

                {/* PreOrder & BackOrder Message */}
                <Box className={'preandbackOrderMessage'}>
                    <Text
                        className={'preandbackOrderAvailability'}
                        h={'20px'}
                        fontSize={{base: '12px', sm: '10px', md: '12px'}}
                        lineHeight={5}
                        m={{base: '4px 0 0', md: '4px 0'}}
                        textAlign={{base: 'left', md: 'center'}}
                        whiteSpace="nowrap"
                    >
                        {(productDetail?.inventory?.backorderable === true ||
                            productDetail?.inventory?.preorderable === true) &&
                            productDetail?.inventory?.ats > 0 &&
                            productDetail?.inventory?.inStockDate &&
                            productDetail?.inventory?.stockLevel === 0 &&
                            (() => {
                                const date = formattedDate(
                                    new Date(productDetail.inventory.inStockDate)
                                )
                                return date === UNCONFIRMED_DATE ? (
                                    <FormattedMessage
                                        defaultMessage="Backorder date to be announced"
                                        id="back_order_date_unknown"
                                        {...styles.backandPreOrder}
                                    />
                                ) : (
                                    <FormattedMessage
                                        defaultMessage="Release date : {date}"
                                        id="back_order_instock_date"
                                        {...styles.backandPreOrder}
                                        values={{date}}
                                    />
                                )
                            })()}
                    </Text>
                </Box>
            </Box>
        </Box>
    )
}

ProductTile.displayName = 'ProductTile'

ProductTile.propTypes = {
    /**
     * The product search hit that will be represented in this
     * component.
     */
    product: PropTypes.shape({
        currency: PropTypes.string,
        c_customerGroups: PropTypes.array,
        image: PropTypes.shape({
            alt: PropTypes.string,
            disBaseLink: PropTypes.string,
            link: PropTypes.string
        }),
        id: PropTypes.string,
        imageGroups: PropTypes.array,
        price: PropTypes.number,
        priceRanges: PropTypes.array,
        tieredPrices: PropTypes.array,
        // `name` is present and localized when `product` is provided by a RecommendedProducts component
        // (from Shopper Products `getProducts` endpoint), but is not present when `product` is
        // provided by a ProductList component.
        // See: https://developer.salesforce.com/docs/commerce/commerce-api/references/shopper-products?meta=getProducts
        name: PropTypes.string,
        // `productName` is localized when provided by a ProductList component (from Shopper Search
        // `productSearch` endpoint), but is NOT localized when provided by a RecommendedProducts
        // component (from Einstein Recommendations `getRecommendations` endpoint).
        // See: https://developer.salesforce.com/docs/commerce/commerce-api/references/shopper-search?meta=productSearch
        // See: https://developer.salesforce.com/docs/commerce/einstein-api/references/einstein-api-quick-start-guide?meta=getRecommendations
        // Note: useEinstein() transforms snake_case property names from the API response to camelCase
        productName: PropTypes.string,
        productId: PropTypes.string,
        productPromotions: PropTypes.array,
        productType: PropTypes.object,
        representedProduct: PropTypes.object,
        hitType: PropTypes.string,
        variationAttributes: PropTypes.array,
        variants: PropTypes.array,
        type: PropTypes.shape({
            set: PropTypes.bool,

            bundle: PropTypes.bool,
            item: PropTypes.bool,
            master: PropTypes.bool
        }),
        inventory: PropTypes.object
    }),
    /**
     * Enable adding/removing product as a favourite.
     * Use case: wishlist.
     */
    enableFavourite: PropTypes.bool,
    showHorizontalTileInMobile: PropTypes.bool,
    /**
     * Display the product as a favourite.
     */
    isFavourite: PropTypes.bool,
    /**
     * Callback function to be invoked when the user
     * interacts with favourite icon/button.
     */
    onFavouriteToggle: PropTypes.func,
    /**
     * When displaying a master/variant product, this value represents the variation attribute that is displayed
     * as a swatch below the main image. The default for this property is `color`.
     */
    selectableAttributeId: PropTypes.string,
    dynamicImageProps: PropTypes.object,
    /**
     * Details of badge labels and the corresponding product custom properties that enable badges.
     */
    badgeDetails: PropTypes.array,
    source: PropTypes.string,
    productPosition: PropTypes.number
}

export default ProductTile
